<script setup lang="ts">
import type { NuxtError } from '#app'
import { fa_ir } from '@nuxt/ui/locale'
import AppColorModeToggle from '~/components/kit/ColorModeToggle.vue'

const props = defineProps<{
  error: NuxtError
}>()

usePersianLayout()

const statusCode = computed(() => Number(props.error?.statusCode) || 500)

const reason = computed(() => {
  const data = props.error?.data as { reason?: string } | undefined
  if (data?.reason === 'empty') return 'empty'
  if (data?.reason === 'site') return 'site'
  const msg = `${props.error?.statusMessage ?? ''} ${props.error?.message ?? ''}`
  if (/محتوا|بدون محتوا|no blocks|empty/i.test(msg)) return 'empty'
  if (/سایت یافت نشد|site not found/i.test(msg)) return 'site'
  return null
})

const copy = computed(() => {
  if (reason.value === 'empty') {
    return {
      icon: 'i-lucide-file-stack',
      codeLabel: 'بدون محتوا',
      title: 'این صفحه هنوز محتوایی ندارد',
      message:
        'آدرس درست است، اما محتوایی برای نمایش تنظیم نشده. به صفحهٔ اصلی برگردید یا بعداً دوباره سر بزنید.',
    }
  }
  if (reason.value === 'site') {
    return {
      icon: 'i-lucide-globe-off',
      codeLabel: 'سایت یافت نشد',
      title: 'این آدرس به سایتی وصل نیست',
      message:
        'دامنه یا مسیر واردشده در آرادکور یافت نشد. لینک را بررسی کنید یا به خانه برگردید.',
    }
  }
  if (statusCode.value === 404) {
    return {
      icon: 'i-lucide-map-pin-off',
      codeLabel: '۴۰۴',
      title: 'صفحه پیدا نشد',
      message:
        'مسیری که دنبالش هستید وجود ندارد یا جابه‌جا شده. می‌توانید به صفحهٔ اصلی برگردید.',
    }
  }
  if (statusCode.value === 403) {
    return {
      icon: 'i-lucide-shield-off',
      codeLabel: '۴۰۳',
      title: 'دسترسی مجاز نیست',
      message: 'برای مشاهدهٔ این بخش مجوز لازم را ندارید.',
    }
  }
  if (statusCode.value >= 500) {
    return {
      icon: 'i-lucide-cloud-off',
      codeLabel: String(statusCode.value),
      title: 'مشکلی پیش آمد',
      message:
        'سرور موقتاً پاسخگو نیست. چند لحظه صبر کنید و دوباره تلاش کنید.',
    }
  }
  return {
    icon: 'i-lucide-circle-alert',
    codeLabel: String(statusCode.value),
    title: props.error?.statusMessage || 'خطا',
    message:
      (typeof props.error?.message === 'string' && props.error.message)
      || 'لطفاً دوباره تلاش کنید یا به صفحهٔ اصلی برگردید.',
  }
})

const displayError = computed(() => ({
  statusCode: statusCode.value,
  statusMessage: copy.value.title,
  message: copy.value.message,
}))

const clearButton = {
  size: 'lg' as const,
  color: 'primary' as const,
  variant: 'solid' as const,
  icon: 'i-lucide-home',
  label: 'بازگشت به خانه',
  class: 'rounded-xl px-6 font-semibold shadow-lg shadow-primary/20',
}

function goBack() {
  const previous = import.meta.client ? window.history.state.back : null
  const redirect = typeof previous === 'string'
    && previous.startsWith('/')
    && !previous.startsWith('//')
    ? previous
    : '/'
  clearError({ redirect })
}
</script>

<template>
  <UApp :locale="fa_ir">
    <div
      dir="rtl"
      lang="fa"
      class="error-shell relative flex min-h-dvh flex-col overflow-hidden"
    >
      <div
        class="pointer-events-none absolute inset-0"
        aria-hidden="true"
      >
        <div class="error-shell__glow absolute inset-0" />
        <div class="error-shell__orb error-shell__orb--a" />
        <div class="error-shell__orb error-shell__orb--b" />
        <div class="error-shell__grid absolute inset-0 opacity-[0.35] dark:opacity-[0.2]" />
      </div>

      <header class="error-shell__brand relative z-10 mx-auto flex w-full max-w-5xl items-center gap-3 px-4 pt-6 sm:px-6 sm:pt-8">
        <NuxtLink
          to="/"
          class="group inline-flex items-center gap-2.5 no-underline"
          @click="clearError({ redirect: '/' })"
        >
          <span
            class="flex size-10 items-center justify-center rounded-xl bg-primary text-white shadow-md shadow-primary/30 transition duration-300 group-hover:scale-[1.03] group-hover:shadow-lg group-hover:shadow-primary/35"
          >
            <UIcon
              name="i-lucide-sparkles"
              class="size-5"
            />
          </span>
            <span class="text-lg font-bold tracking-tight text-default">
              AradCore
            </span>
        </NuxtLink>
        <div class="ms-auto">
          <AppColorModeToggle />
        </div>
      </header>

      <UError
        :error="displayError"
        :clear="false"
        redirect="/"
        class="error-shell__panel relative z-10 flex-1 px-4"
        :ui="{
          root: 'min-h-0 flex flex-col items-center justify-center text-center py-12 sm:py-16',
          leading: 'mb-5 flex items-center justify-center',
          statusCode: 'text-sm font-semibold tracking-wide text-primary',
          statusMessage: 'mt-2 text-3xl sm:text-4xl font-bold text-highlighted text-balance',
          message: 'mt-4 max-w-md text-base sm:text-lg text-muted text-balance leading-relaxed',
          links: 'mt-9 flex flex-wrap items-center justify-center gap-3',
        }"
      >
        <template #leading>
          <div class="error-shell__icon relative flex size-16 items-center justify-center sm:size-18">
            <span class="absolute inset-0 rounded-2xl bg-primary/10 ring-1 ring-primary/15" />
            <span class="error-shell__icon-pulse absolute inset-0 rounded-2xl bg-primary/15" />
            <UIcon
              :name="copy.icon"
              class="relative size-8 text-primary sm:size-9"
            />
          </div>
        </template>

        <template #statusCode>
          <span class="inline-flex items-center gap-2">
            <span class="h-px w-6 bg-primary/40" />
            {{ copy.codeLabel }}
            <span class="h-px w-6 bg-primary/40" />
          </span>
        </template>

        <template #links>
          <UButton
            v-bind="clearButton"
            to="/"
            @click="clearError({ redirect: '/' })"
          />
          <UButton
            color="neutral"
            variant="ghost"
            size="lg"
            icon="i-lucide-arrow-right"
            class="rounded-xl font-medium"
            @click="goBack"
          >
            صفحه قبل
          </UButton>
        </template>
      </UError>

      <footer class="relative z-10 pb-6 text-center text-xs text-muted">
        پلتفرم ساخت سایت پزشکی آرادکور
      </footer>
    </div>
  </UApp>
</template>

<style scoped>
.error-shell {
  font-family: 'IranYekan', sans-serif;
  background: var(--ui-bg);
}

.error-shell__glow {
  background:
    radial-gradient(ellipse 70% 45% at 50% -10%, rgb(37 99 235 / 0.16), transparent 55%),
    radial-gradient(ellipse 45% 35% at 100% 100%, rgb(15 23 42 / 0.06), transparent 50%),
    radial-gradient(ellipse 40% 30% at 0% 80%, rgb(59 130 246 / 0.1), transparent 45%),
    linear-gradient(180deg, #f8fafc 0%, #ffffff 42%, #f1f5f9 100%);
}

:global(.dark) .error-shell__glow {
  background:
    radial-gradient(ellipse 70% 45% at 50% -10%, rgb(37 99 235 / 0.22), transparent 55%),
    radial-gradient(ellipse 45% 35% at 100% 100%, rgb(15 23 42 / 0.55), transparent 50%),
    linear-gradient(180deg, #0b1220 0%, #0f172a 48%, #111827 100%);
}

.error-shell__orb {
  position: absolute;
  border-radius: 9999px;
  filter: blur(48px);
  opacity: 0.45;
  animation: error-orb-drift 14s ease-in-out infinite alternate;
}

.error-shell__orb--a {
  width: 18rem;
  height: 18rem;
  top: 12%;
  inset-inline-start: -4rem;
  background: rgb(37 99 235 / 0.22);
}

.error-shell__orb--b {
  width: 14rem;
  height: 14rem;
  bottom: 8%;
  inset-inline-end: -3rem;
  background: rgb(15 23 42 / 0.12);
  animation-delay: -4s;
  animation-duration: 18s;
}

:global(.dark) .error-shell__orb--a {
  background: rgb(37 99 235 / 0.28);
}

:global(.dark) .error-shell__orb--b {
  background: rgb(59 130 246 / 0.12);
}

.error-shell__grid {
  background-image:
    linear-gradient(rgb(15 23 42 / 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgb(15 23 42 / 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse 70% 60% at 50% 45%, #000 20%, transparent 75%);
}

:global(.dark) .error-shell__grid {
  background-image:
    linear-gradient(rgb(148 163 184 / 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgb(148 163 184 / 0.06) 1px, transparent 1px);
}

.error-shell__brand {
  animation: error-enter 0.65s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.error-shell__panel {
  animation: error-enter 0.75s cubic-bezier(0.22, 1, 0.36, 1) 0.08s both;
}

.error-shell__icon-pulse {
  animation: error-icon-pulse 2.8s ease-in-out infinite;
}

@keyframes error-enter {
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes error-orb-drift {
  from {
    transform: translate3d(0, 0, 0) scale(1);
  }
  to {
    transform: translate3d(1.5rem, 1rem, 0) scale(1.08);
  }
}

@keyframes error-icon-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.55;
  }
  50% {
    transform: scale(1.12);
    opacity: 0.2;
  }
}

@media (prefers-reduced-motion: reduce) {
  .error-shell__brand,
  .error-shell__panel,
  .error-shell__orb,
  .error-shell__icon-pulse {
    animation: none;
  }
}
</style>
